import { isAnonymousUser, selectUser } from "redux/authReducer";
import { connect } from "react-redux";
import { useEffect, useMemo, useState } from "react";
import { State } from "redux/store";
import {
  Dataset,
  DatasetsFilterValue,
  DatasetSortEnum,
  DatasetTypeEnum,
  DatasetStatus,
  InventoryFilterData,
} from "types";

import { getDatasets } from "redux/Datasets/selectors";
import FilterDropdownWrapper from "./FilterDropdownWrapper";
import FilterDropdown from "./FilterDropdown";
import SearchContainer from "./SearchContainer";

import styles from "./style.module.scss";
import uniq from "lodash/uniq";
import { useFeatureFlagService } from "../../../../hooks/useFeatureFlagService";
import { DATASETS_JOB_SETUP_RECIPE_FILTERS } from "../../../../helpers/constants/filters";

interface DatasetsFiltersProps {
  datasetsCountText: string;
  filterData: InventoryFilterData;
  setFilterData: (data: InventoryFilterData) => void;
  handleClearFilters: () => void;
  datasets: Dataset[];
}

type InventoryFilterType =
  | "sortValue"
  | "datasetType"
  | "datasetStatus"
  | "datasetJob"
  | "datasetRecipe"
  | "datasetSetup";

const DATASET_TYPES = [
  { id: DatasetTypeEnum.All, text: "All" },
  { id: DatasetTypeEnum.Private, text: "Private" },
  { id: DatasetTypeEnum.Public, text: "Public" },
];
const DATASET_STATUS_OPTIONS = [
  { id: DatasetStatus.READY, text: "Ready" },
  { id: DatasetStatus.DRAFT, text: "Draft" },
  { id: DatasetStatus.ERROR, text: "Error" },
  { id: DatasetStatus.INDEXING, text: "Indexing" },
  { id: DatasetStatus.READ_ONLY, text: "Read only" },
  { id: DatasetStatus.PARTIAL_INDEX, text: "Partial index" },
];
const SORT_OPTIONS = [
  { id: "status", text: "Status" },
  { id: "date", text: "Date Created" },
  { id: "size", text: "Size" },
  { id: "name", text: "Name" },
];

const resetOptions = (
  options: Array<{ id: string }>,
  prev: DatasetsFilterValue[],
) =>
  options.map(({ id }) => ({
    id,
    selected: Boolean(prev.find((v) => v.id === id)?.selected),
  }));

type MultiValuesFilterProps = ReturnType<typeof useGetMultiValuesFilterProps>;

const useGetMultiValuesFilterProps = (
  options: Array<{ id: any; text: string }>,
  values: DatasetsFilterValue[],
) => {
  const [showOptions, setShowOptions] = useState(false);

  const selectedOptions = options.filter((option) =>
    values.find((status) => status.id === option.id && status.selected),
  );
  const filterText = selectedOptions.length > 0 ? selectedOptions[0].text : "";

  const additionalCount =
    selectedOptions.length > 1 ? selectedOptions.length - 1 : 0;

  const selectedOptionsIds = selectedOptions.map(({ id }) => id);

  return {
    showOptions,
    setShowOptions,
    filterText,
    selectedOptionsIds,
    additionalCount,
  };
};

const DatasetsFilters = ({
  filterData,
  setFilterData,
  datasetsCountText,
  handleClearFilters,
  datasets,
}: DatasetsFiltersProps) => {
  const showDatasetsJobSetupRecipeFilters = useFeatureFlagService(
    DATASETS_JOB_SETUP_RECIPE_FILTERS,
  ).enabled;
  const [showDatasetTypeOptions, setShowDatasetTypeOptions] = useState(false);
  const [showSortValueOptions, setShowSortValueOptions] = useState(false);

  const {
    datasetStatus,
    datasetType,
    sortValue,
    datasetJob,
    datasetSetup,
    datasetRecipe,
  } = filterData;
  const user = selectUser();
  const { jobsOptions, setupOptions, recipeOptions } = useMemo(() => {
    const getValuesOptions = (
      key: "Job Name" | "Setup Name" | "Recipes Name",
    ) =>
      uniq(
        datasets
          .map((d) => d.metadata?.camtek_db_export?.[key])
          .filter(Boolean)
          .flat(),
      ).map((id) => ({ id, text: id })) || [];
    return {
      jobsOptions: getValuesOptions("Job Name"),
      setupOptions: getValuesOptions("Setup Name"),
      recipeOptions: getValuesOptions("Recipes Name"),
    };
  }, [datasets]);

  useEffect(() => {
    setFilterData({
      ...filterData,
      datasetJob: resetOptions(jobsOptions, filterData.datasetJob),
      datasetSetup: resetOptions(setupOptions, filterData.datasetSetup),
      datasetRecipe: resetOptions(recipeOptions, filterData.datasetRecipe),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobsOptions, recipeOptions, setupOptions]);

  const datasetStatusParams = useGetMultiValuesFilterProps(
    DATASET_STATUS_OPTIONS,
    datasetStatus,
  );
  const datasetJobParams = useGetMultiValuesFilterProps(
    jobsOptions,
    datasetJob,
  );
  const datasetSetupParams = useGetMultiValuesFilterProps(
    setupOptions,
    datasetSetup,
  );
  const datasetRecipeParams = useGetMultiValuesFilterProps(
    recipeOptions,
    datasetRecipe,
  );

  const datasetTypeFilterText = filterData.datasetType
    ? filterData.datasetType
    : "";
  const sortValueFilterText = SORT_OPTIONS.filter(
    (option) => sortValue === option.id,
  ).map((option) => option.text);

  const handleFilterSelection = (
    id: string | null,
    type: InventoryFilterType,
    filterProps?: MultiValuesFilterProps,
  ) => {
    if (type === "datasetType") {
      if (id !== datasetType) {
        setFilterData({ ...filterData, datasetType: id as DatasetTypeEnum });
      }
      setShowDatasetTypeOptions(false);
    } else if (type === "sortValue") {
      if (id !== sortValue) {
        setFilterData({ ...filterData, sortValue: id as DatasetSortEnum });
      }
      setShowSortValueOptions(false);
    } else {
      const areAllSelected =
        filterProps!.selectedOptionsIds.length === filterData[type].length;
      if (id === "All") {
        const updatedValues = filterData[type].map((type) => {
          return { ...type, selected: areAllSelected ? false : true };
        });

        setFilterData({ ...filterData, [type]: updatedValues });
      } else {
        let updatedValues = filterData[type].map((status) => {
          return {
            ...status,
            selected: status.id === id ? !status.selected : status.selected,
          };
        });

        setFilterData({ ...filterData, [type]: updatedValues });
      }
    }
  };

  return (
    <div className={styles.datasetFilterContainer}>
      <div className={styles.leftContainer}>
        <SearchContainer
          filterData={filterData}
          setFilterData={setFilterData}
        />
        {/* DatasetType Filter */}
        {!(user === null || isAnonymousUser()) && (
          <FilterDropdownWrapper
            title={"Dataset Type"}
            filterSelectionText={datasetTypeFilterText}
            showDropdown={showDatasetTypeOptions}
            setShowDropdown={setShowDatasetTypeOptions}
          >
            <FilterDropdown
              selectedOptions={[]}
              options={DATASET_TYPES}
              selectedOption={datasetType}
              handleSelection={(id: string | null) =>
                handleFilterSelection(id, "datasetType")
              }
            />
          </FilterDropdownWrapper>
        )}

        {/* DatasetStatus Filter */}
        <FilterDropdownWrapper
          title={"Dataset Status"}
          filterSelectionText={datasetStatusParams.filterText}
          showDropdown={datasetStatusParams.showOptions}
          setShowDropdown={datasetStatusParams.setShowOptions}
          additionalCount={datasetStatusParams.additionalCount}
        >
          <FilterDropdown
            selectedOption={null}
            allowMultiSelect={true}
            defaultOptionText={"All"}
            options={DATASET_STATUS_OPTIONS}
            selectedOptions={datasetStatusParams.selectedOptionsIds}
            handleSelection={(id: string | null) =>
              handleFilterSelection(id, "datasetStatus", datasetStatusParams)
            }
          />
        </FilterDropdownWrapper>

        {showDatasetsJobSetupRecipeFilters && (
          <>
            <FilterDropdownWrapper
              title={"Dataset Job"}
              filterSelectionText={datasetJobParams.filterText}
              showDropdown={datasetJobParams.showOptions}
              setShowDropdown={datasetJobParams.setShowOptions}
              additionalCount={datasetJobParams.additionalCount}
            >
              <FilterDropdown
                showSearch
                selectedOption={null}
                allowMultiSelect={true}
                defaultOptionText={"All"}
                options={jobsOptions}
                selectedOptions={datasetJobParams.selectedOptionsIds}
                handleSelection={(id: string | null) =>
                  handleFilterSelection(id, "datasetJob", datasetJobParams)
                }
              />
            </FilterDropdownWrapper>

            <FilterDropdownWrapper
              title={"Dataset Setup"}
              filterSelectionText={datasetSetupParams.filterText}
              showDropdown={datasetSetupParams.showOptions}
              setShowDropdown={datasetSetupParams.setShowOptions}
              additionalCount={datasetSetupParams.additionalCount}
            >
              <FilterDropdown
                showSearch
                selectedOption={null}
                allowMultiSelect={true}
                defaultOptionText={"All"}
                options={setupOptions}
                selectedOptions={datasetSetupParams.selectedOptionsIds}
                handleSelection={(id: string | null) =>
                  handleFilterSelection(id, "datasetSetup", datasetSetupParams)
                }
              />
            </FilterDropdownWrapper>

            <FilterDropdownWrapper
              title={"Dataset Recipe"}
              filterSelectionText={datasetRecipeParams.filterText}
              showDropdown={datasetRecipeParams.showOptions}
              setShowDropdown={datasetRecipeParams.setShowOptions}
              additionalCount={datasetRecipeParams.additionalCount}
            >
              <FilterDropdown
                showSearch
                selectedOption={null}
                allowMultiSelect={true}
                defaultOptionText={"All"}
                options={recipeOptions}
                selectedOptions={datasetRecipeParams.selectedOptionsIds}
                handleSelection={(id: string | null) =>
                  handleFilterSelection(
                    id,
                    "datasetRecipe",
                    datasetRecipeParams,
                  )
                }
              />
            </FilterDropdownWrapper>
          </>
        )}

        {/* Sort Filter */}
        <FilterDropdownWrapper
          title={"Sort by"}
          filterSelectionText={sortValueFilterText[0]}
          showDropdown={showSortValueOptions}
          setShowDropdown={setShowSortValueOptions}
        >
          <FilterDropdown
            selectedOptions={[]}
            options={SORT_OPTIONS}
            selectedOption={sortValue}
            handleSelection={(id: string | null) =>
              handleFilterSelection(id, "sortValue")
            }
          />
        </FilterDropdownWrapper>

        <div
          onClick={handleClearFilters}
          className={styles.clearSelection}
          data-testid="dataset-inventory-clear-filters-btn"
        >
          Clear Selection
        </div>
      </div>
      <div className={styles.rightContainer}>{datasetsCountText}</div>
    </div>
  );
};

const mapStatesToProps = (state: State) => {
  return {
    datasets: getDatasets(state),
  };
};

export default connect(mapStatesToProps)(DatasetsFilters);
